/**
 * The primitives: the values every message can read, as three lists of
 * definitions, and the procedures every message can call, as a fourth.
 *
 * A primitive is a name every message can read, such as `{_title}` or
 * `{_recipientInformalName}`. Each is defined by an expression over the facts
 * of the frame it is defined in, and the three lists are the three frames a
 * message's environment is extended with, outermost first:
 *
 * 1. `SHARE_DEFINITIONS`, over `ShareFacts`: what is fixed for the whole
 *    share session — the topic, today, the sender and their update.
 * 2. `INTENT_DEFINITIONS`, over `IntentFacts` too: what one intent's
 *    messages share — the reshare's connection and story.
 * 3. `RECIPIENT_DEFINITIONS`, over `RecipientFacts` too: what one message's
 *    recipients and leg decide — their names and the link.
 *
 * Extending an environment evaluates each definition in its list over the
 * facts and binds the value under its name (see
 * `EnvironmentLogic.extendWithDefinitions`). So a definition reads the facts
 * of its own frame and the frames around it; a share definition cannot read
 * a recipient, because `ShareFacts` has none. Template text never sees the
 * facts, only the names.
 *
 * Adding a primitive = adding one definition to the list whose facts it
 * reads. `PRIMITIVE_NAMES`, `PrimitiveName`, the docs the builder offers
 * and the YAML schema validator all derive from the three lists (see the
 * end of this file).
 *
 * A primitive procedure, such as `truncate`, `ensureFullStop` or `uppercase`, is defined
 * the same way, in `PROCEDURE_DEFINITIONS`: a name, a doc, and what it does.
 * As in SICP's `primitive-procedures` (§4.1.4), they are bound in the base
 * environment, the frame every other frame is built on, and a token applies
 * one with parentheses: `{truncate(_body, 150, true)}`. Its parameters are
 * typed, so validation catches a call with the wrong number or kind of
 * operands before a message is composed. Adding a primitive procedure =
 * adding one definition there.
 *
 * A topic type can also define names of its own over these: see
 * `topic_garden/topic_type_def.ts`.
 */

import { isValid } from 'date-fns';
import { ASCII_LOGO, IMPRESSION_GOAL_MAX_DEFAULT } from '@/modules/config';
import { TopicLogic } from '@/modules/topic/logic';
import { ContactLogic } from '@/modules/contact/logic';
import { TOPIC_TYPES } from '@/modules/topic/types';
import type { Topic } from '@/modules/topic/types';
import type { Contact } from '@/modules/contact/types';

/** What the whole share session is about: the share frame's facts. */
export interface ShareFacts {
  readonly topic: Topic;
  readonly now: Date;
  /** The sender's profile name, which also signs the leg link. */
  readonly profileName: string;
  readonly isUpdateFlow: boolean;
  /** The sender's ≤150-char summary of what changed; set only in the update flow (#427). */
  readonly updateSummary?: string;
  /**
   * The global impression-goal ceiling (#447), for clamping the topic's goal at
   * render time. Supplied, not derived: it is config (FEATURE_FLAGS_JSON via
   * useAppConfig), which a pure module cannot read. Absent falls back to
   * IMPRESSION_GOAL_MAX_DEFAULT, which is the live value whenever the flag is
   * unset.
   */
  readonly impressionGoalMax?: number;
  /** People reached so far — a news run's total, or the topic's. */
  readonly runImpressionCount?: number;
  /**
   * The sender's preferred pronouns exactly as stored on their profile, e.g.
   * "she/her". The three forms below are derived from it, pre-resolved by the
   * caller (see ReshareStoryLogic.pronounForms) so this module stays free of
   * share-module imports.
   */
  readonly preferredPronouns?: string;
  /** Bare subject form, e.g. "she" / "he" / "they". */
  readonly subjectPronoun?: string;
  /** Subject + contracted copula, e.g. "she's" / "he's" / "they're". */
  readonly subjectPronounIs?: string;
  /** Possessive form, e.g. "her" / "his" / "their". */
  readonly possessivePronoun?: string;
}

/** What one intent's messages share: the intent frame's own facts. */
export interface IntentFacts {
  /**
   * The resharer's first-person connection to whoever sent them the leg they
   * pass on ("I work with"), asked once per reshare by the composer's
   * `ConnectionField`. Set only in the reshare sharing mode.
   */
  readonly connectionToPrevLeg?: string;
  /**
   * The server-rendered story of the leg being composed (#447), shaped by the
   * topic's reshare story settings: who the sender got it from and how they
   * know them, how far that person is from the starting sender, and the
   * senders' allowed details when the settings include them (`I reshared this from
   * my friend Fred. He's 2 degrees separated from Sally, the author.`). Whole
   * sentences of its own. Set only in the reshare sharing mode.
   */
  readonly reshareStory?: string;
}

/** Who one message goes to: the recipient frame's own facts. */
export interface RecipientFacts {
  readonly contacts: readonly Contact[];
  /** When set, _topicUrl produces a leg-redirect URL (/b/{legId}) instead of the topic URL. */
  readonly legId?: string;
}

/**
 * One primitive: its name, what a person calls it, and the expression
 * that defines it — a primitive over the facts of its frame and the frames
 * around it.
 *
 * The `doc` is a lowercase noun phrase that reads after a verb — a control
 * says "Include recipient’s informal name" — so it names the value, not the
 * primitive's spelling.
 */
export interface PrimitiveDefinition<F, N extends string = string> {
  readonly name: N;
  readonly doc: string;
  /**
   * Whether the value changes from one recipient to the next. Such a value
   * is only an example of what a message says, never what every message says.
   * Left out, it is false.
   */
  readonly perRecipient?: boolean;
  /**
   * Whether the value names whoever sends the message. If true, creating a
   * hypothetical message with this primitive will mean injecting a fake sender
   * name. Left out, it is false.
   */
  readonly perSender?: boolean;
  bootstrapValue(facts: F): string;
}

type Spec<F> = Omit<PrimitiveDefinition<F>, 'name'>;

// One definer per frame, so each definition's `facts` is typed as what that
// frame can see and its name stays a literal for `PrimitiveName`.
function definer<F>() {
  return <N extends `_${string}`>(
    name: N,
    spec: Spec<F>
  ): PrimitiveDefinition<F, N> => ({ name, ...spec });
}

const sharePrimitive = definer<ShareFacts>();
const intentPrimitive = definer<ShareFacts & IntentFacts>();
const recipientPrimitive = definer<ShareFacts & IntentFacts & RecipientFacts>();

// ── private helpers ──────────────────────────────────────────────────────────

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function ymdInZone(d: Date, tz: string): { y: number; m: number; d: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (t: string) => Number(parts.find(p => p.type === t)?.value);
  return { y: get('year'), m: get('month'), d: get('day') };
}

function daysUntil(target: Date, now: Date, tz: string): number {
  const t = ymdInZone(target, tz);
  const n = ymdInZone(now, tz);
  const tUtc = Date.UTC(t.y, t.m - 1, t.d);
  const nUtc = Date.UTC(n.y, n.m - 1, n.d);
  return Math.round((tUtc - nUtc) / MS_PER_DAY);
}

function weekdayInZone(d: Date, tz: string): string {
  return d.toLocaleDateString('en-US', { weekday: 'long', timeZone: tz });
}

function dayMonthInZone(d: Date, tz: string, includeYear: boolean): string {
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    ...(includeYear ? { year: 'numeric' } : {}),
    timeZone: tz,
  });
}

function crossesWeekend(target: Date, now: Date, tz: string): boolean {
  const days = daysUntil(target, now, tz);
  const nowYmd = ymdInZone(now, tz);
  for (let offset = 1; offset < days; offset += 1) {
    const weekday = new Date(
      Date.UTC(nowYmd.y, nowYmd.m - 1, nowYmd.d + offset)
    ).getUTCDay();
    if (weekday === 0 || weekday === 6) return true;
  }
  return false;
}

function smartDatePhrase(
  target: Date | undefined,
  now: Date,
  tz: string
): string {
  if (target === undefined) return '';
  if (!isValid(target)) return 'invalid';
  const days = daysUntil(target, now, tz);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === 7) return 'in one week';
  if (days === 14) return 'in two weeks';
  if (days >= 2 && days <= 6) {
    const weekday = weekdayInZone(target, tz);
    return crossesWeekend(target, now, tz)
      ? `next ${weekday}`
      : `this ${weekday}`;
  }

  const targetYmd = ymdInZone(target, tz);
  const nowYmd = ymdInZone(now, tz);
  return `on ${dayMonthInZone(target, tz, targetYmd.y !== nowYmd.y)}`;
}

function smartDatetimePhrase(
  target: Date | undefined,
  now: Date,
  tz: string
): string {
  if (target === undefined) return '';
  if (!isValid(target)) return 'invalid';
  return `${smartDatePhrase(target, now, tz)} at ${TopicLogic.formatTopicTime(target, tz)}`;
}

function recipientFirstName(c: Contact): string {
  return c.firstName.trim() || ContactLogic.displayName(c);
}

/** `value` ending as a sentence: a full stop added unless it already ends one. */
function ensureFullStop(value: string): string {
  return value && !/[.!?…]$/.test(value) ? `${value}.` : value;
}

/**
 * The first `length` characters of `value`, counting an emoji as one. With
 * `ellipsis`, a value that loses characters ends in "..." instead, still
 * within `length`.
 */
function truncate(value: string, length: number, ellipsis: boolean): string {
  const chars = Array.from(value);
  if (chars.length <= length) return value;
  return ellipsis
    ? `${chars.slice(0, Math.max(0, length - 3)).join('')}...`
    : chars.slice(0, Math.max(0, length)).join('');
}

/**
 * The byline line of a newspaper article, the name in capitals, "By JOHN
 * DOE", or '' for no name.
 */
function newspaperStyleByline(byline: string): string {
  return byline ? `By ${byline.toUpperCase()}\n` : '';
}

/**
 * The dateline that opens a newspaper article's first paragraph, the place in
 * capitals and the day in AP style, "DENVER, Oct. 11 — ", with whichever of
 * the two are given, or '' for neither.
 */
function newspaperStyleDateline(date: string, location: string): string {
  const where = [location.toUpperCase(), newspaperStyleDate(date)]
    .filter(Boolean)
    .join(', ');
  return where ? `${where} — ` : '';
}

/** Months as the AP stylebook abbreviates them before a day. */
const AP_MONTHS: Record<string, string> = {
  jan: 'Jan.',
  feb: 'Feb.',
  mar: 'March',
  apr: 'April',
  may: 'May',
  jun: 'June',
  jul: 'July',
  aug: 'Aug.',
  sep: 'Sept.',
  oct: 'Oct.',
  nov: 'Nov.',
  dec: 'Dec.',
};

/**
 * A date such as `_startDate`'s "Sunday, Oct 11, 2026" in AP style, "Oct. 11",
 * keeping the year ("Oct. 11, 2025") when it isn't the current one. Text that
 * isn't such a date comes back unchanged.
 */
function newspaperStyleDate(date: string): string {
  const match =
    /^(?:[A-Za-z]+,\s*)?([A-Za-z]{3})[A-Za-z]*\.?\s+(\d{1,2}),?\s+(\d{4})$/.exec(
      date.trim()
    );
  const month = match && AP_MONTHS[match[1].toLowerCase()];
  if (!match || !month) return date;
  const [, , day, year] = match;
  return Number(year) === new Date().getFullYear()
    ? `${month} ${Number(day)}`
    : `${month} ${Number(day)}, ${year}`;
}

/**
 * A body laid out in AP style: each non-blank line a paragraph, the blank
 * lines between them dropped, and every paragraph indented two spaces — all
 * but the first when it follows a dateline, which opens it instead.
 */
function newspaperStyleParagraph(
  body: string,
  hasDatelinePrefix: boolean
): string {
  return body
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map((paragraph, i) =>
      i === 0 && hasDatelinePrefix ? paragraph : `  ${paragraph}`
    )
    .join('\n');
}

// ── the share frame ───────────────────────────────────────────────────────────

export const SHARE_DEFINITIONS = [
  sharePrimitive('_title', {
    doc: 'topic title',
    bootstrapValue: ({ topic }) => topic.title.trim(),
  }),
  // A news topic's byline, which its messages lay out as a newspaper
  // article's with the `newspaperStyleByline` procedure. Empty when the topic
  // leaves it out.
  sharePrimitive('_byline', {
    doc: 'byline',
    bootstrapValue: ({ topic }) => topic.byline?.trim() ?? '',
  }),
  // The topic's location, a short place such as a city or a meeting point.
  // News defines it as `_datelineLocation`, which `newspaperStyleDateline`
  // lays out with the topic's `_startDate` (news's `_datelineDate`), and the
  // beautification and rally types as `_meetingLocation`. Empty when the topic leaves it out.
  sharePrimitive('_location', {
    doc: 'location',
    bootstrapValue: ({ topic }) => topic.location?.trim() ?? '',
  }),
  // The topic's body is the news a news topic passes from person to person, so
  // a message quotes it rather than a note of the sender's own.
  // Quoted whole: a message that wants an excerpt calls
  // `{truncate(_body, 150, true)}`.
  sharePrimitive('_body', {
    doc: 'topic body',
    bootstrapValue: ({ topic }) => topic.body?.trim() ?? '',
  }),
  sharePrimitive('_profileName', {
    doc: 'my name',
    perSender: true,
    bootstrapValue: ({ profileName }) => profileName,
  }),
  sharePrimitive('_asciiLogo', {
    doc: 'Snowball logo',
    bootstrapValue: () => ASCII_LOGO,
  }),
  sharePrimitive('_startDatetime', {
    doc: 'start date and time',
    bootstrapValue: ({ topic }) =>
      TopicLogic.formatTopicDatetime(
        TopicLogic.startTimeAsDateObj(topic),
        topic.timezone
      ),
  }),
  sharePrimitive('_endDatetime', {
    doc: 'end date and time',
    bootstrapValue: ({ topic }) =>
      TopicLogic.formatTopicDatetime(
        TopicLogic.endTimeAsDateObj(topic),
        topic.timezone
      ),
  }),
  sharePrimitive('_startDate', {
    doc: 'start date',
    bootstrapValue: ({ topic }) =>
      TopicLogic.formatTopicDate(
        TopicLogic.startDateAsDateObj(topic),
        topic.timezone
      ),
  }),
  sharePrimitive('_endDate', {
    doc: 'end date',
    bootstrapValue: ({ topic }) =>
      TopicLogic.formatTopicDate(
        TopicLogic.endDateAsDateObj(topic),
        topic.timezone
      ),
  }),
  sharePrimitive('_startTime', {
    doc: 'start time',
    bootstrapValue: ({ topic }) =>
      TopicLogic.formatTopicTime(
        TopicLogic.startTimeAsDateObj(topic),
        topic.timezone
      ),
  }),
  sharePrimitive('_endTime', {
    doc: 'end time',
    bootstrapValue: ({ topic }) =>
      TopicLogic.formatTopicTime(
        TopicLogic.endTimeAsDateObj(topic),
        topic.timezone
      ),
  }),
  sharePrimitive('_smartStartDatetime', {
    doc: 'start date and time, relative to today',
    bootstrapValue: ({ topic, now }) =>
      smartDatetimePhrase(
        TopicLogic.startTimeAsDateObj(topic),
        now,
        topic.timezone
      ),
  }),
  sharePrimitive('_smartStartDate', {
    doc: 'start date, relative to today',
    bootstrapValue: ({ topic, now }) =>
      smartDatePhrase(
        TopicLogic.startDateAsDateObj(topic),
        now,
        topic.timezone
      ),
  }),
  sharePrimitive('_smartEndDatetime', {
    doc: 'end date and time, relative to today',
    bootstrapValue: ({ topic, now }) =>
      smartDatetimePhrase(
        TopicLogic.endTimeAsDateObj(topic),
        now,
        topic.timezone
      ),
  }),
  sharePrimitive('_smartEndDate', {
    doc: 'end date, relative to today',
    bootstrapValue: ({ topic, now }) =>
      smartDatePhrase(TopicLogic.endDateAsDateObj(topic), now, topic.timezone),
  }),
  sharePrimitive('_updateSummary', {
    doc: 'my summary of the changes',
    bootstrapValue: ({ updateSummary }) => updateSummary?.trim() ?? '',
  }),
  // Rendered as its own paragraph in the intro bodyWrap (#427): the leading
  // "\n\n" sets it off from the pitch above, the bodyWrap's own break sets it
  // off from the CTA below (a blank line on both sides), and
  // `ensureFullStop` caps the user-authored summary so it reads as a
  // finished sentence. Empty (and no extra newlines) outside the update flow.
  sharePrimitive('_whatsNewIfUpdating', {
    doc: 'what’s new, in an update',
    bootstrapValue: ({ isUpdateFlow, updateSummary }) => {
      const summary = updateSummary?.trim();
      return isUpdateFlow && summary
        ? `\n\nEven if you heard about this, you might not know: ${ensureFullStop(summary)}`
        : '';
    },
  }),
  sharePrimitive('_topicType', {
    doc: 'kind of topic',
    bootstrapValue: ({ topic }) => {
      const found = TOPIC_TYPES.find(t => t.id === (topic.type ?? 'event'));
      return found?.messageSubstitution ?? 'topic';
    },
  }),

  // ── impression + pronoun primitives (#447) ─────────────────────────────────
  // General context, available to every topic type: any topic can set an
  // impression goal, and any sender has pronouns. Each renders empty when its
  // fact is unset.

  // Derived from the topic, not supplied: the goal is a first-class field on
  // it (#447), so every caller already carries it. Clamped the same way the
  // read view clamps, so a message never names a goal the topic can no longer
  // reach.
  sharePrimitive('_impressionGoal', {
    doc: 'impression goal',
    bootstrapValue: ({ topic, impressionGoalMax }) => {
      const goal = TopicLogic.effectiveImpressionGoal(
        topic.impressionGoal,
        impressionGoalMax ?? IMPRESSION_GOAL_MAX_DEFAULT
      );
      return goal != null ? String(goal) : '';
    },
  }),
  sharePrimitive('_runImpressionCount', {
    doc: 'number of people reached',
    bootstrapValue: ({ runImpressionCount }) =>
      runImpressionCount != null ? String(runImpressionCount) : '',
  }),
  // The impression count WITH its noun, singular-safe ("1 person" / "5
  // people") — what the goal sentence embeds so a count of 1 never reads "1
  // people". Mirrors ReshareStoryLogic.peoplePhrase (this module stays free of
  // share-module imports).
  sharePrimitive('_runImpressionPeople', {
    doc: 'people reached so far',
    bootstrapValue: ({ runImpressionCount }) =>
      runImpressionCount != null
        ? runImpressionCount === 1
          ? '1 person'
          : `${runImpressionCount} people`
        : '',
  }),
  // The sender's preferred pronouns as they chose them on their profile
  // ("she/her"), for a template that wants to name the set rather than use one
  // of its forms. The three forms below are what a sentence about the sender
  // needs.
  sharePrimitive('_preferredPronouns', {
    doc: 'my preferred pronouns',
    bootstrapValue: ({ preferredPronouns }) => preferredPronouns ?? '',
  }),
  sharePrimitive('_subjectPronoun', {
    doc: 'my subject pronoun',
    bootstrapValue: ({ subjectPronoun }) => subjectPronoun ?? '',
  }),
  // Subject + contracted copula ("she's" / "they're"), so the goal sentence
  // stays grammatical for they/them where "{_subjectPronoun}'s" would not.
  sharePrimitive('_subjectPronounIs', {
    doc: 'my subject pronoun with “is”',
    bootstrapValue: ({ subjectPronounIs }) => subjectPronounIs ?? '',
  }),
  sharePrimitive('_possessivePronoun', {
    doc: 'my possessive pronoun',
    bootstrapValue: ({ possessivePronoun }) => possessivePronoun ?? '',
  }),
] as const;

// ── the intent frame ─────────────────────────────────────────────────────────
// Reshare-only primitives (#447), empty outside the reshare sharing mode: they
// describe a leg being passed on.

export const INTENT_DEFINITIONS = [
  intentPrimitive('_connectionToPrevLeg', {
    doc: 'my connection to whoever sent it to me',
    bootstrapValue: ({ connectionToPrevLeg }) =>
      connectionToPrevLeg?.trim() ?? '',
  }),
  intentPrimitive('_reshareStory', {
    doc: 'story of how this reached me',
    bootstrapValue: ({ reshareStory }) => reshareStory ?? '',
  }),
] as const;

// ── the recipient frame ──────────────────────────────────────────────────────

export const RECIPIENT_DEFINITIONS = [
  recipientPrimitive('_recipientNames', {
    doc: 'recipients’ first names',
    perRecipient: true,
    bootstrapValue: ({ contacts }) =>
      contacts.map(recipientFirstName).join(', '),
  }),
  recipientPrimitive('_recipientInformalName', {
    doc: 'recipient’s informal name',
    perRecipient: true,
    bootstrapValue: ({ contacts }) =>
      contacts[0] ? ContactLogic.informalName(contacts[0]) : '',
  }),
  // The leg it links to is the message's own.
  recipientPrimitive('_topicUrl', {
    doc: 'link to the topic',
    bootstrapValue: ({ topic, profileName, legId }) => {
      // No update marker in the link: whether an update leg brought the
      // recipient here is derived server-side from the leg record itself
      // (#427).
      const base = legId
        ? `${window.location.origin}/b/${legId}`
        : `${window.location.origin}/topics/${encodeURIComponent(topic.id)}`;
      const params = new URLSearchParams();
      params.set('f', profileName || '_');
      const query = params.toString();
      return query ? `${base}?${query}` : base;
    },
  }),
] as const;

// ── the primitive procedures ─────────────────────────────────────────────────

/** The kinds of value an operand can be. */
export type ValueType = 'string' | 'number' | 'boolean';

/** A value an expression evaluates to: text, or an operand's number or flag. */
export type Value = string | number | boolean;

type ValueOf<T> = T extends 'string'
  ? string
  : T extends 'number'
    ? number
    : T extends 'boolean'
      ? boolean
      : never;

/**
 * One primitive procedure: its name, what a person calls what it does, the
 * kind of each operand it takes, and its implementation: what it does with
 * them (SICP's `primitive-implementation`). Every procedure
 * returns text, since what a token evaluates to is printed.
 */
export interface ProcedureDefinition<N extends string = string> {
  readonly name: N;
  readonly doc: string;
  readonly parameters: readonly ValueType[];
  implementation(operands: readonly Value[]): string;
}

/** The operands a procedure with parameters `P` is applied to, as their own types. */
type Operands<P extends readonly ValueType[]> = Extract<
  { [K in keyof P]: ValueOf<P[K]> },
  readonly unknown[]
>;

// `P` is inferred as a tuple, so `implementation` sees each operand as its
// own type.
function primitiveProcedure<
  N extends string,
  P extends readonly ValueType[] | [],
>(
  name: N,
  spec: {
    readonly doc: string;
    readonly parameters: P;
    implementation(...operands: Operands<P>): string;
  }
): ProcedureDefinition<N> {
  return {
    name,
    doc: spec.doc,
    parameters: spec.parameters,
    implementation: operands =>
      spec.implementation(...(operands as Operands<P>)),
  };
}

export const PROCEDURE_DEFINITIONS = [
  primitiveProcedure('truncate', {
    doc: 'text cut to a length',
    parameters: ['string', 'number', 'boolean'],
    implementation: truncate,
  }),
  primitiveProcedure('ensureFullStop', {
    doc: 'text ending as a sentence',
    parameters: ['string'],
    implementation: ensureFullStop,
  }),
  primitiveProcedure('uppercase', {
    doc: 'text in capitals',
    parameters: ['string'],
    implementation: value => value.toUpperCase(),
  }),
  primitiveProcedure('newspaperStyleDate', {
    doc: 'date in newspaper style',
    parameters: ['string'],
    implementation: newspaperStyleDate,
  }),
  primitiveProcedure('newspaperStyleParagraph', {
    doc: 'text in newspaper paragraphs',
    parameters: ['string', 'boolean'],
    implementation: newspaperStyleParagraph,
  }),
  // The two lines of a newspaper article's header, each empty when what it
  // gives is: `{newspaperStyleByline(_byline)}` and
  // `{newspaperStyleDateline(_datelineDate, _datelineLocation)}`.
  primitiveProcedure('newspaperStyleByline', {
    doc: 'byline line for a byline',
    parameters: ['string'],
    implementation: newspaperStyleByline,
  }),
  primitiveProcedure('newspaperStyleDateline', {
    doc: 'dateline for a date and location',
    parameters: ['string', 'string'],
    implementation: newspaperStyleDateline,
  }),
] as const;

/** Every name a token may call as a procedure. */
export type ProcedureName = (typeof PROCEDURE_DEFINITIONS)[number]['name'];

export const PROCEDURES: ReadonlyMap<string, ProcedureDefinition> = new Map(
  PROCEDURE_DEFINITIONS.map(definition => [definition.name, definition])
);

// ── the primitives by name ──────────────────────────────────────────────────

const ALL_DEFINITIONS = [
  ...SHARE_DEFINITIONS,
  ...INTENT_DEFINITIONS,
  ...RECIPIENT_DEFINITIONS,
];

/** Every name a `{token}` may use for a primitive value. */
export type PrimitiveName = (typeof ALL_DEFINITIONS)[number]['name'];

/** What a person calls a primitive, and how it varies between messages. */
export interface PrimitiveInfo {
  readonly doc: string;
  readonly perRecipient?: boolean;
  readonly perSender?: boolean;
}

export const PRIMITIVES = Object.fromEntries(
  ALL_DEFINITIONS.map(({ name, doc, perRecipient, perSender }) => [
    name,
    { doc, perRecipient, perSender },
  ])
) as Readonly<Record<PrimitiveName, PrimitiveInfo>>;

/** Every primitive name, alphabetically, as the builder's Insert menu lists them. */
export const PRIMITIVE_NAMES: readonly PrimitiveName[] = ALL_DEFINITIONS.map(
  ({ name }) => name
).sort();

export const PRIMITIVE_NAMES_SET: ReadonlySet<string> = new Set(
  PRIMITIVE_NAMES
);
